import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { ArrowRight, Mail, Phone, User, Building, Package } from 'lucide-react';
import { toast } from 'sonner';

const QuoteFormSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sectionRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    machineType: '',
    partName: '',
    message: ''
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentSection = sectionRef.current;
    
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      const currentSection = sectionRef.current;
    
    if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      console.log('Quote Request:', formData);
      
      toast.success('Quote Request Submitted!', {
        description: "Thank you! We'll get back to you within 24 hours.",
        duration: 5000,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        machineType: '',
        partName: '',
        message: ''
      });
      
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <section ref={sectionRef} id="quote" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-16 transform transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="inline-block bg-green-100 text-green-500 px-4 py-2 rounded-full font-semibold mb-4">
              Get Instant Quote
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Request a Quick Quote
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Fill in your details and get a quote within minutes during working hours.
            </p>
          </div>

          {/* Form */}
          <div className={`bg-white rounded-2xl shadow-2xl p-8 lg:p-12 border border-gray-100 transform transition-all duration-700 delay-200 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name and Email */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-700 font-medium flex items-center">
                    <User className="w-4 h-4 mr-2 text-green-600" />
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="border-gray-300 focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-green-600" />
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    className="border-gray-300 focus:border-green-500 focus:ring-green-500"
                  />
                </div>
              </div>

              {/* Phone and Machine Type */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-700 font-medium flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-green-600" />
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="border-gray-300 focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="machineType" className="text-gray-700 font-medium flex items-center">
                    <Building className="w-4 h-4 mr-2 text-green-600" />
                    Machine Type
                  </Label>
                  <Input
                    id="machineType"
                    name="machineType"
                    type="text"
                    value={formData.machineType}
                    onChange={handleChange}
                    placeholder="JCB, Excavator, Loader, etc."
                    className="border-gray-300 focus:border-green-500 focus:ring-green-500"
                  />
                </div>
              </div>

              {/* Part Name */}
              <div className="space-y-2">
                <Label htmlFor="partName" className="text-gray-700 font-medium flex items-center">
                  <Package className="w-4 h-4 mr-2 text-green-600" />
                  Part Name / Number
                </Label>
                <Input
                  id="partName"
                  name="partName"
                  type="text"
                  value={formData.partName}
                  onChange={handleChange}
                  placeholder="e.g., Hydraulic Pump, Oil Filter, etc."
                  className="border-gray-300 focus:border-green-500 focus:ring-green-500"
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-gray-700 font-medium">
                  Additional Details
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us more about your requirements..."
                  rows={4}
                  className="border-gray-300 focus:border-green-500 focus:ring-green-500 resize-none"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-green-500 hover:bg-green-600 text-white text-lg font-semibold py-6 shadow-lg hover:shadow-xl transition-all group"
              >
                {isSubmitting ? 'Submitting...' : 'Get Price Now'}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>

              <p className="text-sm text-gray-500 text-center">
                ⚡ Response within 30 minutes during working hours
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteFormSection;
